import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import DialogNames from '~/constants/DialogNames'
import {
  createInventoryOrderItemReceipts,
  getInventoryOrderError,
  getInventoryOrdersIsUpdating,
} from '~/store/duck/inventoryOrders'
import {
  getInventoryDamageReasons,
  getPackageType,
  getProductSizeUnit,
} from '~/store/reducers/constants'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import {
  calculatePackageCount,
  getOneUomPluralized,
  getPluralPackageLabel,
} from '../../inventoryUtils'
import { getReceiptQuantitiesByReason, isReceivingDisabled } from '../utils'
import { useDraftReceipts } from './hooks/useDraftReceipts'
import {
  type ReceiptFieldGroup,
  useReceiptFields,
} from './hooks/useReceiptFields'
import {
  OrderItemReceiptConfirmModal,
  type Quantities,
} from './OrderItemReceiptConfirmModal'
import { OrderItemReceiptsList } from './OrderItemReceiptsList'
import { OrderItemReceiptsPageHeader } from './OrderItemReceiptsPageHeader'
import { OrderItemReceiptsSummary } from './OrderItemReceiptsSummary'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import styles from './OrderItemReceiptsPage.module.scss'

interface ValidationError {
  message: string
  reason?: string
}

const getTotalReceiptQuantities = (receipts: ReceiptFieldGroup[]) => {
  const { totalQuantityRejected, totalQuantityReceived } = receipts.reduce(
    (totals, { fields }) => {
      const quantity = fields.receivedQuantity.value || 0
      if (fields.rejected.value) {
        totals.totalQuantityRejected += quantity
      } else {
        totals.totalQuantityReceived += quantity
      }
      return totals
    },
    { totalQuantityRejected: 0, totalQuantityReceived: 0 },
  )

  return { totalQuantityRejected, totalQuantityReceived }
}

interface OrderItemReceiptsPageProps {
  inventoryOrder: InventoryOrder
  inventoryOrderItem: InventoryOrderItem
}

export const OrderItemReceiptsPage = ({
  inventoryOrder,
  inventoryOrderItem,
}: OrderItemReceiptsPageProps) => {
  const { t } = useTranslation(['Common', 'Admin'])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const error = useSelector(getInventoryOrderError)
  const receiveReasons = useSelector(getInventoryDamageReasons)

  const { receiptFields } = useReceiptFields(inventoryOrderItem.receipts)
  const {
    draftReceiptFields,
    addDraftReceipt,
    duplicateDraftReceipt,
    deleteDraftReceipt,
    validateDraftReceipts,
    getDraftReceiptsForSubmission,
  } = useDraftReceipts()

  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const PackageType = useSelector(getPackageType)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )
  const [confirmModalQuantities, setConfirmModalQuantities] =
    useState<Quantities | null>(null)

  const [openConfirmCloseDialog, closeConfirmCloseDialog] = useDialog(
    DialogNames.CONFIRM_CLOSE_DIALOG,
  )

  const { totalQuantityRejected, totalQuantityReceived } =
    getTotalReceiptQuantities([...receiptFields, ...draftReceiptFields])

  const {
    totalQuantityRejected: draftQuantityRejected,
    totalQuantityReceived: draftQuantityReceived,
  } = getTotalReceiptQuantities(draftReceiptFields)

  const isViewOnly = isReceivingDisabled(
    inventoryOrder.status,
    inventoryOrderItem.status,
  )

  const { perPackageAmount } = inventoryOrderItem

  const navigateToOrderItemsPage = () => {
    navigate(`/admin/catalog/inventories/orders/${inventoryOrder.id}`)
  }

  const closeAfterSaving = useCloseAfterCreation(() => {
    if (!error) {
      navigateToOrderItemsPage()
    }
  }, getInventoryOrdersIsUpdating)

  const submitReceipts = () => {
    const receiptsToSave = getDraftReceiptsForSubmission()
    closeAfterSaving()
    dispatch(
      createInventoryOrderItemReceipts(
        inventoryOrder.id,
        inventoryOrderItem.id,
        receiptsToSave,
      ),
    )
  }

  const getDraftQuantity = () => draftQuantityReceived + draftQuantityRejected

  const getPlural = (unitName: string, amount: number) =>
    getOneUomPluralized(unitName, amount)

  const hasIncompleteFields = () =>
    draftReceiptFields.some(({ fields }) =>
      Object.values(fields).some((field) => !field.valid),
    )

  const hasInvalidUomQuantity = () =>
    getDraftQuantity() % perPackageAmount !== 0

  const buildValidationErrors = (): ValidationError[] => {
    const errors: ValidationError[] = []

    const packageType = getPluralPackageLabel(
      inventoryOrderItem.packageType.id,
      PackageType,
    )
    const productSizeUnit = LanguageUtils.getConstantTranslatedName(
      inventoryOrderItem.packageUnits.id,
      ProductSizeUnit,
    )

    const productSizeUnitForPackage = getPlural(
      productSizeUnit,
      perPackageAmount,
    )
    const packageSizeWithUnit = `${perPackageAmount} ${productSizeUnitForPackage}`

    if (hasIncompleteFields()) {
      errors.push({
        message: t('Admin:CATALOG.ORDERS.RECEIVING_INCOMPLETE_FIELDS'),
      })
    }

    if (hasInvalidUomQuantity()) {
      const totalQuantity = getDraftQuantity()
      const quantityRatio = calculatePackageCount(
        totalQuantity,
        perPackageAmount,
      )
      const packageTypeRatio = getPlural(packageType, quantityRatio)
      const packageTypeSingular = getPlural(packageType, 1)
      const productSizeUnitTotal = getPlural(productSizeUnit, totalQuantity)

      errors.push({
        message: t('Admin:CATALOG.ORDERS.RECEIVING_INVALID_UOM_QTY', {
          packageSizeWithUnit,
          packageTypeSingular,
        }),
        reason: t('Admin:CATALOG.ORDERS.RECEIVING_INVALID_UOM_QTY_REASON', {
          totalQuantity,
          productSizeUnitTotal,
          quantityRatio,
          packageSizeWithUnit,
          packageTypeRatio,
          packageTypeSingular,
        }),
      })
    }

    return errors
  }

  const handleSaveDraftReceipts = () => {
    if (!draftReceiptFields.length) {
      return
    }

    const hasInvalidDraftReceipts = !validateDraftReceipts()
    const errors = buildValidationErrors()
    const hasErrors = errors.length > 0
    setValidationErrors(errors)

    if (hasInvalidDraftReceipts || hasErrors) {
      return
    }

    const quantities = getReceiptQuantitiesByReason(
      [...receiptFields, ...draftReceiptFields],
      inventoryOrderItem.orderedQuantity * inventoryOrderItem.perPackageAmount,
      receiveReasons,
    )

    const hasOverage = quantities.receivingOverage > 0
    const hasRejections =
      quantities.rejectingOverage > 0 ||
      quantities.rejectingDamaged > 0 ||
      quantities.rejectingExpired > 0 ||
      quantities.rejectingRecalled > 0 ||
      quantities.rejectingOther > 0

    if (hasOverage || hasRejections) {
      setConfirmModalQuantities(quantities)
      return
    }

    setValidationErrors([])
    submitReceipts()
  }

  const handleCancel = () => {
    if (draftReceiptFields.length > 0) {
      openConfirmCloseDialog({
        // Save
        onOk: () => {
          handleSaveDraftReceipts()
          closeConfirmCloseDialog()
        },
        // Don't Save
        onNotOk: () => {
          navigateToOrderItemsPage()
          closeConfirmCloseDialog()
        },
      })
      return
    }
    navigateToOrderItemsPage()
  }

  useEffect(() => {
    const shouldCreateInitialDraftReceipt =
      !isViewOnly && !(receiptFields.length || draftReceiptFields.length)
    if (shouldCreateInitialDraftReceipt) {
      addDraftReceipt()
    }
  }, [isViewOnly, receiptFields, draftReceiptFields])

  const isSaveDisabled = isUpdating || !draftReceiptFields.length || isViewOnly

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <OrderItemReceiptsPageHeader
          inventoryOrder={inventoryOrder}
          inventoryOrderItem={inventoryOrderItem}
        />
        {validationErrors.length > 0 &&
          validationErrors.map((entry) => (
            <KibTopLevelAlert
              className={styles.errorAlert}
              heading={entry.message}
              key={entry.message}
              severity="error"
            >
              {entry.reason}
            </KibTopLevelAlert>
          ))}
        <div className={styles.receiptsList}>
          <OrderItemReceiptsList
            addDraftReceipt={addDraftReceipt}
            deleteDraftReceipt={deleteDraftReceipt}
            draftReceiptFields={draftReceiptFields}
            duplicateDraftReceipt={duplicateDraftReceipt}
            inventoryOrderItem={inventoryOrderItem}
            isViewOnly={isViewOnly}
            receiptFields={receiptFields}
          />
        </div>
        {isViewOnly && (
          <div className={styles.backButton}>
            <KibButtonNew onClick={navigateToOrderItemsPage}>
              {t('Common:GO_BACK')}
            </KibButtonNew>
          </div>
        )}
      </div>
      {!isViewOnly && (
        <div className={styles.footer}>
          <div className={styles.buttons}>
            <KibButtonNew
              disabled={isSaveDisabled}
              loading={isUpdating}
              onClick={handleSaveDraftReceipts}
            >
              {t('Common:SAVE_ACTION')}
            </KibButtonNew>
            <KibButtonNew emphasis="secondary" onClick={handleCancel}>
              {t('Common:CANCEL_ACTION')}
            </KibButtonNew>
          </div>
          <OrderItemReceiptsSummary
            inventoryOrderItem={inventoryOrderItem}
            totalQuantityReceived={totalQuantityReceived}
            totalQuantityRejected={totalQuantityRejected}
          />
        </div>
      )}
      {confirmModalQuantities && (
        <OrderItemReceiptConfirmModal
          open
          item={inventoryOrderItem}
          quantities={confirmModalQuantities}
          onClose={() => setConfirmModalQuantities(null)}
          onMakeChanges={() => setConfirmModalQuantities(null)}
          onReceive={() => {
            setConfirmModalQuantities(null)
            submitReceipts()
          }}
        />
      )}
    </div>
  )
}
